// Every claim on this page is traceable to the Lexeme app itself — its README,
// its App Store metadata, or the code that implements the feature. If a feature
// is built but not yet shipped to users, it is labelled as such rather than
// advertised. Nothing here should overstate what a download actually gives you.

export const hero = {
  eyebrow: 'A Saar Labs project · iOS and Android',
  title: 'Tap. Guess.\nRead your books.',
  standfirst:
    'Lexeme is a calm EPUB reader for people who learn a language by reading real books. Tap any word for an offline meaning — conjugations and all — and it quizzes you before it answers.',
  primaryCta: 'See how a lookup works',
  secondaryCta: 'Look at the screens',
  meta: 'A personal project · Not released · No account, no tracking',
};

// The app's own positioning, from docs/app-store/faq.md ("Lexeme is not…").
// Saying what a product refuses to be is more useful than another feature list.
export const positioning = {
  title: 'The reading half of your learning stack.',
  body:
    'Lexeme does not sell you lessons, a content catalogue, or a streak. It does one job: keep you inside a real book in a language you are still learning, and make the moment you get stuck on a word cost you three seconds instead of three minutes.',
  notList: [
    {
      name: 'Not LingQ',
      body: 'No content library, no imports gated behind Premium, no blue-and-yellow word colouring across everything you read. You bring the book.',
    },
    {
      name: 'Not Beelinguapp',
      body: 'No curated graded-reader catalogue and no native-language column beside every sentence. You read the original.',
    },
    {
      name: 'Not KOReader',
      body: 'No StarDict plug-in hunt and no dictionary configuration before your first lookup. Install a pack, tap a word, done.',
    },
    {
      name: 'Not Anki',
      body: 'Lexeme exports to Anki rather than replacing it. Save while reading; drill wherever you already drill.',
    },
  ],
};

export const features = [
  {
    id: 'books' as const,
    index: 'I',
    title: 'Your books, or 75,000 free ones',
    description:
      'Import any DRM-free EPUB or PDF from Files, iCloud, or Drive — or search Project Gutenberg inside the app and download straight into your library. No browser detour, no store account.',
  },
  {
    id: 'tap' as const,
    index: 'II',
    title: 'Conjugations resolved offline',
    description:
      'Tap andiamo and Lexeme walks it back to andare, "to go", without a network. Direct hit, then lemma, then form-of resolution — and only if all three miss does it ask the internet.',
  },
  {
    id: 'quiz' as const,
    index: 'III',
    title: 'Learn mode guesses first',
    description:
      'Every lookup becomes a four-option question, with distractors drawn from real same-part-of-speech meanings in the dictionary. Retrieval practice at the exact moment the word matters.',
  },
  {
    id: 'vocab' as const,
    index: 'IV',
    title: 'Words keep their sentence',
    description:
      'Saved vocabulary carries the line you met it in, plus the lemma it came from. Export the lot to CSV or straight to an Anki deck when you want to drill it properly.',
  },
  {
    id: 'audio' as const,
    index: 'V',
    title: 'Read along with the audiobook',
    description:
      'Attach an MP3, M4A or M4B to any book. Mini-player, lock-screen controls, and manual sync markers that pull the text along with the narration.',
  },
  {
    id: 'sepia' as const,
    index: 'VI',
    title: 'Sepia, and nothing blinking',
    description:
      'Warm paper by default, with light and dark alongside it. Georgia at 19pt, a 720-pixel column, page-flip or scroll. Set it once and forget the interface exists.',
  },
];

// The offline-first cascade in lib/services/dictionary — the single most
// distinctive thing the app does, and the thing the old site never mentioned.
export const lookupChain = {
  eyebrow: 'What happens when you tap',
  title: 'Four steps, and three of them work in airplane mode.',
  body:
    'Most readers hand the word to a translation API and hope you have signal. Lexeme tries everything it already holds on your device first, in order, and only reaches for the network as a last resort.',
  word: 'andiamo',
  ipa: '/anˈdja.mo/',
  lemma: 'andare',
  gloss: 'to go',
  sentence: '— Andiamo, presto! gridò il capitano.',
  steps: [
    {
      step: '01',
      title: 'Direct hit',
      description: 'The exact form is looked up in the installed pack. Most common words stop here.',
      offline: true,
    },
    {
      step: '02',
      title: 'Lemmatisation',
      description: 'No exact entry? The word is reduced to its dictionary form and tried again.',
      offline: true,
    },
    {
      step: '03',
      title: 'Form-of resolution',
      description:
        'The inflection table maps andiamo → andare and pulls the base meaning back up, accents folded so vado and dissi resolve too.',
      offline: true,
    },
    {
      step: '04',
      title: 'Online fallback',
      description: 'Only now — and only if you are connected — does Lexeme ask MyMemory for a translation.',
      offline: false,
    },
  ],
};

export const learnMode = {
  eyebrow: 'Learn mode',
  title: 'It makes you guess before it tells you.',
  body:
    'Turn Learn mode on and a tap opens a question instead of an answer. Four options, one right, three pulled from genuine dictionary meanings that share the tapped word\'s part of speech — so "to consume" and "to feed" sit next to "to eat" and you actually have to think.',
  points: [
    'Distractors come from real same-part-of-speech glosses, never from a random word list.',
    'The sentence you tapped in stays on screen, so you can reason from context.',
    'Guess, reveal, save, and you are back on the page — the book never leaves the screen.',
    'Prefer the plain answer? Learn mode is a switch in the reader toolbar.',
  ],
};

export const gutenberg = {
  eyebrow: 'Where the books come from',
  title: 'Three ways to fill a library.',
  body:
    'Lexeme has no store, so nothing is locked to it. Books arrive from wherever you already keep them, and leave the same way.',
  sources: [
    {
      title: 'Project Gutenberg, in-app',
      description:
        'Search the full catalogue and download without opening a browser. Many titles ship a matching LibriVox audiobook — Lexeme grabs the chaptered set and attaches it to the book.',
      badge: 'Free · 75,000+ titles',
    },
    {
      title: 'Your own files',
      description:
        'EPUB 2, EPUB 3 and PDF, DRM-free, from Files, iCloud Drive, Dropbox or anywhere else the system file picker can reach.',
      badge: 'EPUB · PDF',
    },
    {
      title: 'Audio you already own',
      description:
        'Attach an MP3, M4A, M4B or AAC file to any book in your library and it becomes a read-along.',
      badge: 'MP3 · M4A · M4B · AAC',
    },
  ],
};

export const audiobook = {
  eyebrow: 'Read along',
  title: 'Let the narrator set the pace.',
  body:
    'Attach an audiobook and Lexeme keeps a mini-player docked under the page, themed to match whatever palette you are reading in. Lock-screen and headphone controls work as you would expect. Drop a sync marker at the start of a chapter and the text follows the audio from there.',
  footnote:
    'On-device forced alignment for Italian, which highlights each word as it is read and seeks the audio when you tap one. It runs entirely on your phone — no audio is uploaded — and needs a one-time speech pack that has not been published yet.',
};

export const vocabulary = {
  eyebrow: 'Vocabulary',
  title: 'A record of every word that stopped you.',
  body:
    'Saved words are grouped by the book they came from and keep three things: the form you actually met, the lemma behind it, and the sentence that gave it away. That is what makes a card worth reviewing a week later.',
  exports: [
    { label: 'Anki', description: 'A deck, ready to import, with context on the back.' },
    { label: 'CSV', description: 'Plain columns for any other SRS or a spreadsheet.' },
    { label: 'Attribution', description: 'Exports carry the CC BY-SA dictionary notice automatically.' },
  ],
};

// From frontend/public/dictionaries/catalog.json — the same manifest the app
// reads at install time. Keep these in step with the published pack.
export const dictionaryStats = {
  language: 'Italian → English',
  headwords: '651,002',
  size: '147 MB',
  version: 'v1.1',
  description:
    'The Italian hero pack is built from Wiktionary (via kaikki.org) and FreeDict, plus a full inflection table and GloVe neighbours for Learn mode distractors. It downloads from lexeme.uk on request, verifies against a SHA-512 in the catalogue, and then never needs the network again.',
};

export const languageTiers = [
  {
    tier: 'Full morphology',
    languages: 'Italian',
    description:
      'Hero pack: inflection tables, form-of resolution, part-of-speech aware Learn mode distractors, IPA. Conjugated forms resolve offline.',
    published: true,
  },
  {
    tier: 'Basic offline glosses',
    languages: '20+ languages via FreeDict',
    description:
      'Direct dictionary lookup from packs fetched live from freedict.org — German, Spanish, French, Portuguese, Dutch and more. Conjugated forms may fall through to the online lookup.',
    published: true,
  },
  {
    tier: 'Built, not yet published',
    languages: 'Spanish · French hero packs',
    description:
      'The same build pipeline produces Spanish and French hero packs. They are deliberately absent from the catalogue until they are downloadable — the app will not claim a capability it cannot deliver.',
    published: false,
  },
];

// Specification table. Real numbers from lib/core/theme/reading_theme.dart and
// lib/models/reader_settings.dart — a colophon, not marketing copy.
export const spec = [
  { label: 'Book formats', value: 'EPUB 2, EPUB 3, PDF · DRM-free' },
  { label: 'Audio formats', value: 'MP3, M4A, M4B, AAC' },
  { label: 'Themes', value: 'Sepia (default), Light, Dark' },
  { label: 'Reading fonts', value: 'Georgia, Times New Roman, Helvetica Neue' },
  { label: 'Font size', value: '13–34 pt' },
  { label: 'Line height', value: '1.2–2.2' },
  { label: 'Column width', value: '720 px maximum' },
  { label: 'Page turns', value: 'Page-flip or continuous scroll' },
  { label: 'Rendering', value: 'Native text, not a WebView' },
  { label: 'Built with', value: 'Flutter · iOS and Android' },
];

export const privacy = {
  eyebrow: 'Privacy',
  title: 'Nothing to sign up for. Nothing to send us.',
  points: [
    { title: 'No account', body: 'There is no login, because there is no server holding your library.' },
    { title: 'No tracking', body: 'No analytics on what you read, look up, or save.' },
    { title: 'On your device', body: 'Books, vocabulary and dictionaries live in local storage only.' },
    {
      title: 'One optional call',
      body: 'If an offline lookup misses and you are online, the word alone goes to MyMemory. Nothing else leaves.',
    },
  ],
};

export const steps = [
  {
    chapter: 'One',
    title: 'Install a dictionary',
    description:
      'First run offers the Italian hero pack, or any FreeDict language. Skip it and Lexeme still reads — it just asks the network when you tap.',
  },
  {
    chapter: 'Two',
    title: 'Bring a book',
    description:
      'Import an EPUB or PDF you already own, or search Project Gutenberg from the library screen and pull one down for free.',
  },
  {
    chapter: 'Three',
    title: 'Read. Tap. Guess.',
    description:
      'Turn pages in sepia. When a word stops you, tap it, take the guess, save it if it is worth keeping, and carry on down the page.',
  },
  {
    chapter: 'Four',
    title: 'Come back tomorrow',
    description:
      'Lexeme remembers your place. Your saved words are waiting in the Vocabulary tab, ready to export whenever you want to drill them.',
  },
];

export const sisterProjects = [
  {
    name: 'Shablam',
    tagline: '"Shazam for Drag Race" runway anthems',
    description:
      "Identify any RuPaul's Drag Race lip-sync track and dive into performance lore within seconds.",
    links: [
      { label: 'shablam.app', href: 'https://shablam.app' },
      {
        label: 'Android app',
        href: 'https://play.google.com/store/apps/details?id=com.saarlabs.shablam',
      },
    ],
  },
  {
    name: 'Eurovizam',
    tagline: '"Shazam for Eurovision" performances',
    description:
      'Recognize Eurovision entries on the spot and surface trivia, standings, and lyric context.',
    links: [
      {
        label: 'Google Play',
        href: 'https://play.google.com/store/apps/details?id=com.saarlabs.eurovizam',
      },
    ],
  },
];

export type DeviceId = 'iphone' | 'ipad' | 'pixel';

export const devices: {
  id: DeviceId;
  label: string;
  note: string;
  width: number;
  height: number;
}[] = [
  { id: 'iphone', label: 'iPhone', note: 'iPhone 6.7 inch', width: 1320, height: 2868 },
  { id: 'ipad', label: 'iPad', note: 'iPad Pro 12.9 inch', width: 2064, height: 2752 },
  { id: 'pixel', label: 'Android', note: 'Pixel', width: 1440, height: 3040 },
];

// Plate captions. `focus` picks the part of the capture the crop keeps — a full
// phone screen at gallery width would be over 600px tall, so each plate is
// framed on whatever makes it worth looking at.
//
// 06-dictionaries.png is deliberately absent: the captured file shows a
// half-completed screen transition with a loading spinner, and the Android copy
// is byte-identical to the iPhone one. Recapture both and add it back here.
export const plates: {
  id: string;
  index: string;
  title: string;
  description: string;
  alt: string;
  focus: 'top' | 'bottom';
}[] = [
  {
    id: '01-learn-mode',
    index: '01',
    title: 'Guess before you look',
    description:
      'Learn mode turns the tap on mangiava into a four-way question, with the sentence still in view above it.',
    alt: 'Lexeme Learn mode asking what the Italian word mangiava means, with four possible meanings',
    focus: 'bottom',
  },
  {
    id: '02-inflection',
    index: '02',
    title: 'Conjugations resolved offline',
    description:
      'andiamo arrives with its pronunciation, the note "from andare", and the base verb\'s full entry underneath.',
    alt: 'Lexeme lookup sheet showing andiamo resolved to the verb andare with definitions',
    focus: 'top',
  },
  {
    id: '03-reader',
    index: '03',
    title: 'Your books, distraction-free',
    description: 'Sepia paper, a serif face, a measured column, and a toolbar that gets out of the way.',
    alt: 'Lexeme reading an Italian book in sepia theme',
    focus: 'top',
  },
  {
    id: '04-library',
    index: '04',
    title: 'A library you assembled',
    description: 'Imported files and Project Gutenberg downloads sit side by side, progress kept for each.',
    alt: 'Lexeme library grid showing two Italian books downloaded from Project Gutenberg',
    focus: 'top',
  },
  {
    id: '05-vocabulary',
    index: '05',
    title: 'Every word keeps its sentence',
    description: 'Saved words grouped by book, each with its lemma, its meaning, and the line it came from.',
    alt: 'Lexeme vocabulary list showing saved Italian words with lemmas and context sentences',
    focus: 'top',
  },
];

export type ComparisonRow = {
  label: string;
  lexeme: string;
  lingq: string;
  beelinguapp: string;
  koreader: string;
  lexemeHighlight?: boolean;
};

export const comparisonIntro = {
  eyebrow: 'Why Lexeme',
  title: 'Built to finish books, not to sell lessons.',
  description:
    'LingQ, Beelinguapp and KOReader are all good at what they set out to do. Lexeme sets out to do something narrower: get you through the novel already on your shelf, offline, without a subscription.',
};

export const comparisonRows: ComparisonRow[] = [
  {
    label: 'Primary goal',
    lexeme: 'Finish books you bring',
    lingq: 'Build vocabulary from any content',
    beelinguapp: 'Study curated bilingual stories',
    koreader: 'Read anything, configure everything',
  },
  {
    label: 'Your own files',
    lexeme: 'EPUB and PDF, on device',
    lingq: 'Import on desktop, Premium-gated',
    beelinguapp: 'App library only',
    koreader: 'Yes, most formats',
    lexemeHighlight: true,
  },
  {
    label: 'Word lookup',
    lexeme: 'Offline, inflection-aware',
    lingq: 'Tap for instant translation',
    beelinguapp: 'Native-language reference',
    koreader: 'Whatever plug-in you install',
    lexemeHighlight: true,
  },
  {
    label: 'Before the answer',
    lexeme: 'Four-option guess, then reveal',
    lingq: 'Answer shown immediately',
    beelinguapp: 'Translation always visible',
    koreader: 'Answer shown immediately',
    lexemeHighlight: true,
  },
  {
    label: 'Free books built in',
    lexeme: 'Project Gutenberg search',
    lingq: 'Own lesson library',
    beelinguapp: 'Own story library',
    koreader: 'Via OPDS setup',
  },
  {
    label: 'Audiobook read-along',
    lexeme: 'Attach your own audio',
    lingq: 'Lesson audio only',
    beelinguapp: 'Story audio only',
    koreader: 'No',
    lexemeHighlight: true,
  },
  {
    label: 'Export to Anki',
    lexeme: 'Deck or CSV, with context',
    lingq: 'CSV export',
    beelinguapp: 'No',
    koreader: 'Via plug-in',
  },
  {
    label: 'Account required',
    lexeme: 'No',
    lingq: 'Yes',
    beelinguapp: 'Yes',
    koreader: 'No',
    lexemeHighlight: true,
  },
  {
    label: 'Availability',
    lexeme: 'Personal project, not released',
    lingq: 'App Store and Google Play',
    beelinguapp: 'App Store and Google Play',
    koreader: 'Sideload or F-Droid',
  },
  {
    label: 'Price',
    lexeme: 'Free, if it ever ships',
    lingq: 'Free tier capped; ~$10–15/mo',
    beelinguapp: 'Free with limits; ~$7–45/yr',
    koreader: 'Free and open source',
    lexemeHighlight: true,
  },
];

export const faqs = [
  {
    question: 'What exactly is Lexeme?',
    answer:
      'A reader, first. Lexeme opens EPUBs and PDFs you already own, renders them as native text in warm sepia, and lets you tap any word for an offline meaning. Vocabulary and review exist to keep you moving through the book — not to become the thing you do instead of reading.',
  },
  {
    question: 'Which languages actually work well?',
    answer:
      'Italian is the only fully supported language today: its hero pack ships inflection tables, so conjugated and inflected forms resolve offline, and Learn mode can build sensible distractors. Twenty-plus other languages install FreeDict packs for basic offline glosses — good for dictionary forms, less good for conjugations. Spanish and French hero packs are built but not yet published, and Lexeme will not list them until they are downloadable.',
  },
  {
    question: 'Does it work without a connection?',
    answer:
      'Yes, once a dictionary pack is installed. Reading, lookup, lemmatisation, inflection resolution, Learn mode, saving words and exporting all run on device. The only thing that needs the network is the fallback translation when a word is missing from your installed pack — and downloading books or packs in the first place.',
  },
  {
    question: 'What is Learn mode?',
    answer:
      'A switch in the reader toolbar. With it on, tapping a word gives you four possible meanings instead of one definition. The three wrong options are real dictionary glosses that share the tapped word\'s part of speech, so guessing takes actual thought. Guess, reveal, and you are back on the page.',
  },
  {
    question: 'Can I get my words into Anki?',
    answer:
      'Yes. Vocabulary exports as an Anki deck or a CSV, and each entry carries the sentence you met the word in along with the lemma it resolved to. Lexeme deliberately does not try to replace your SRS — read here, drill where you already drill.',
  },
  {
    question: 'Can I use my own audiobook?',
    answer:
      'Attach any MP3, M4A, M4B or AAC file to a book and you get a docked mini-player with lock-screen controls. Manual sync markers let you tie a point in the audio to a point in the text so the page follows the narration. Word-by-word highlighting is in development for Italian and runs entirely on device.',
  },
  {
    question: 'Do you collect anything?',
    answer:
      'No account, no analytics, no reading history leaving the device. Books, saved words and dictionaries are stored locally. The single exception is the optional online fallback: when an offline lookup misses, the word itself is sent to MyMemory for translation.',
  },
  {
    question: 'Can I download it?',
    answer:
      'Not at the moment. Lexeme is a personal project — it was built for one person to read Italian novels with, and there is no public build, no beta and no waitlist. This page exists to describe the app properly in case that ever changes.',
  },
];

export const about = {
  eyebrow: 'About',
  title: 'A Saar Labs experiment about finishing things.',
  body:
    'Most people learning a language own a book in it that they have not finished. Usually they stopped somewhere in the first thirty pages, because looking up the fourth unknown word on a page costs more than the sentence is worth. Lexeme is an attempt to make that cost close to nothing — and then to get out of the way, because the book was always the point. It was written to solve that problem for one reader, and it is used that way today.',
  credits: [
    { label: 'Dictionary data', value: 'Wiktionary via kaikki.org, and FreeDict — CC BY-SA' },
    { label: 'Online fallback', value: 'MyMemory' },
    { label: 'Sample and in-app books', value: 'Project Gutenberg' },
    { label: 'Built with', value: 'Flutter, iOS-first' },
  ],
};

// There is no waitlist, no signup and no store link, because there is nothing
// to sign up for. This page exists to describe the app precisely enough that a
// public release would only need the download link adding.
export const status = {
  eyebrow: 'Status',
  title: 'Built for one reader. This page just explains it.',
  body:
    'Lexeme is not on the App Store or Google Play, and there is no beta to join. It was built for one person to finish Italian novels with, and it works — everything described on this page is implemented and running, apart from the two things marked as unpublished.',
  note:
    'If it is ever released publicly, this page is the description it will ship with. Until then it is a record of what the app does, written against the code rather than an intention.',
  facts: [
    { label: 'Availability', value: 'Not released · no public build' },
    { label: 'Signups', value: 'None — there is nothing to join' },
    { label: 'Accounts', value: 'None, by design' },
    { label: 'Cost, if released', value: 'Free — there is no server to pay for' },
  ],
};
